import { InteractsWithDOM } from '@scripts/core/InteractsWithDOM.js';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * @var {{unavailable: string, available: string}} lbt_thematic_labels
 */

export class ReserveThematic extends InteractsWithDOM {
    PREFIX = 'reservation-popup';

    constructor (domElm) {
        super(domElm);

        this.retrieveElements();
        this.addListeners();
        this.init();
    }

    get isMobile () {
        return window.innerWidth <= 768;
    }

    get selectedDate () {
        return this.domElm.querySelector(`input[name=date]:checked`);
    }

    retrieveElements () {
        this.header = this.domElm.querySelector(`.${this.PREFIX}__header`);
        this.content = this.domElm.querySelector(`.${this.PREFIX}__content`);
        this.price = this.domElm.querySelector(`.${this.PREFIX}__price`);
        this.button = this.domElm.querySelector(`.${this.PREFIX}__cta`);
        this.buttonWeTravel = this.domElm.querySelectorAll(`.${this.PREFIX}__wt-cta`);
        this.dialog = this.domElm.querySelector(`#${this.PREFIX}-dialog`);
        this.iframe = this.domElm.querySelector(`#${this.PREFIX}-iframe`);
        this.dateInputs = this.domElm.querySelectorAll(`input[name="date"]`);
        this.reservationButton = this.domElm.querySelector(`.${this.PREFIX}__header-cta`);
    }

    addListeners () {
        // Écouter les changements de sélection de date
        this.dateInputs.forEach((input) => input.addEventListener('change', () => this.handleInputChange()));

        this.header?.addEventListener('click', () => {
            if (!this.domElm.classList.contains('sticky-bottom')) return;

            this.domElm.classList.toggle('open');
            this.domElm.style.setProperty('--rp-price-height', this.computeFutureHeight(this.price) + 'px');

            if (this.domElm.classList.contains('open')) {
                gsap.to(this.domElm, {
                    bottom: this.isMobile ? 0 : () => Math.max(0, window.innerHeight - 200 - this.domElm.scrollHeight),
                    duration: 0.5,
                    ease: 'power2.inOut'
                });
            } else {
                gsap.to(this.domElm, {
                    bottom: this.getBottomHiddenPosition(),
                    duration: 0.5,
                    ease: 'power2.inOut'
                });
            }
        });

        // To let the span click available for button with WeTravel
        this.buttonWeTravel.forEach((button) => {
            button.querySelector('.button__text')
                ?.addEventListener('click', () => {
                    button.click();
                });
        });

        window.addEventListener('message', (event) => {
            if (event.origin !== 'https://www.wetravel.com') return;

            if (event.data === 'wtrvlCheckoutClosed') {
                this.dialog.close();
                this.iframe.src = '';
                document.body.classList.remove('no-scroll');
            }
        });

        // Click open popup {
        this.reservationButton?.addEventListener('click', (e) => {
            e.preventDefault();
        });
    }

    init () {
        // Select first by default if none is selected
        if (!this.selectedDate) {
            this.dateInputs[0].checked = true;
        }

        this.initGsapAnimations();
    }

    initGsapAnimations () {
        try {
            gsap.registerPlugin(ScrollTrigger);

            gsap.set(this.domElm, {
                bottom: () => -this.domElm.scrollHeight - 25
            });

            if (this.isMobile) {
                this.toggleStickyBottom(true);
            }

            gsap.to(this.domElm, {
                bottom: !this.isMobile
                    ? () => Math.max(0, window.innerHeight - 200 - this.domElm.scrollHeight)
                    : this.getBottomHiddenPosition(),
                ease: 'none',
                scrollTrigger: {
                    trigger: '.presentation',
                    start: 'top bottom',
                    end: this.isMobile ? 'top+=66 bottom' : 'top-=200 top',
                    scrub: true
                }
            });

            if (!this.isMobile) {
                ScrollTrigger.create({
                    trigger: 'section.section-review',
                    start: 'top bottom',
                    end: 'top center',
                    onEnter: () => {
                        gsap.to(this.domElm, {
                            bottom: this.getBottomHiddenPosition(),
                            duration: 0.5,
                            ease: 'power2.inOut'
                        });
                        this.toggleStickyBottom(true);
                    },
                    onLeaveBack: () => {
                        gsap.to(this.domElm, {
                            bottom: () => Math.max(0, window.innerHeight - 200 - this.domElm.scrollHeight),
                            duration: 0.5,
                            ease: 'power2.inOut'
                        });
                        this.toggleStickyBottom(false);
                    }
                });
            }
        } catch (error) {
            console.error('Erreur lors de l\'initialisation GSAP de la popup:', error);
            // Fallback : afficher la popup directement
            this.domElm.style.bottom = '0';
        }
    }

    getBottomHiddenPosition () {
        return () => {
            return this.computeFutureHeight(this.header) - this.computeFutureHeight(this.domElm);
        };
    }

    toggleStickyBottom (add = true) {
        if (add) {
            this.domElm.classList.add('sticky-bottom');
        } else {
            this.domElm.classList.remove('sticky-bottom');
            this.domElm.classList.remove('open');
        }

        this.domElm.style.setProperty('--rp-price-height', this.computeFutureHeight(this.price) + 'px');
    }

    computeFutureHeight (elm) {
        const clone = elm.cloneNode(true);
        // Remove input interference
        clone.querySelectorAll('input').forEach((e) => {
            e.setAttribute('name', e.getAttribute('name') + '_2');
        });

        clone.style.visibility = 'hidden';
        clone.style.position = 'absolute';
        clone.width = elm.offsetWidth;
        this.domElm.appendChild(clone);
        const finalHeight = clone.offsetHeight;
        this.domElm.removeChild(clone);

        return finalHeight;
    }

    handleInputChange () {
        // Remove URL parameter
        this.updateUrl();

        Array.from(this.buttonWeTravel).forEach((e) => {
            if (this.selectedDate.value === e.dataset.uuid) {
                e.classList.add('active');
            } else {
                e.classList.remove('active');
            }
        });
    }

    updateUrl () {
        if (!this.selectedDate) return;

        let newUrl = new URL(location.href);

        newUrl.searchParams.set('date', this.selectedDate.dataset.slug);

        window.history.pushState('object', document.title, newUrl.toString());
    }
}
